import {
  formatExistingDocumentsOutline,
  formatStructureOutline,
} from "./hseDocumentImportUtils";

/**
 * Copy-paste system instructions for LLMs building Promang HSE/KS document bulk JSON.
 * Pass company disciplines + existing documents so the guide avoids duplicates.
 */
export function buildHseDocumentAiGuide(disciplines = [], existingDocuments = []) {
  const outline = formatStructureOutline(disciplines);
  const existingDocs = formatExistingDocumentsOutline(existingDocuments);

  return `You generate **Promang HSE/KS document bulk JSON** for the controlled documents library.

Used in: **HSE/KS → Documents → JSON / AI**.

## Output rules
- Return **one** JSON object only:
  \`{ "create_missing_structure": true, "structure": [ ... ], "documents": [ ... ] }\`
- No markdown fences, no commentary before or after the JSON.
- Do **not** include database \`id\` fields.
- Prefer **existing** Fag/Bransje, Kategori and Kapittel from the company structure below.
- Set \`create_missing_structure: true\` **only when** introducing new discipline, category or chapter. Default is false (reuse existing structure).
- Optional \`atomic: true\` rolls back all documents if any document row fails.
- \`access_type\` defaults to \`all\` (company-wide read/approve), matching normal create.
- **Do not invent documents that already exist.** Skip titles that match (or are near-duplicates of) the existing documents list below, unless the user explicitly asks to revise/replace them.

## Current company structure (use these codes/numbers when possible)

${outline}

## Existing documents (do NOT recreate these)

${existingDocs}

## Top-level fields

| Field | Type | Description |
|-------|------|-------------|
| \`create_missing_structure\` | boolean | Create missing discipline/category/chapter (**default false** — set true when needed) |
| \`atomic\` | boolean | If true, roll back all new documents when any document fails |
| \`access_type\` | string | \`all\` (default) or \`specific\` |
| \`structure\` | array | Optional new Fag → Kategori → Kapittel tree |
| \`documents\` | array | Documents to create (draft status, max 50) |

## Structure schema

\`\`\`json
{
  "discipline": { "code": "VENT_UTF", "name": "Ventilasjon utførende" },
  "categories": [
    {
      "code": "hms",
      "name": "HMS",
      "chapters": [
        { "number": "6", "title": "Utførelse" },
        { "number": "6.1", "title": "Instrukser", "parent_number": "6" }
      ]
    }
  ]
}
\`\`\`

## Document schema

\`\`\`json
{
  "title": "Document title (required)",
  "description": "Short purpose",
  "document_type": "instruction",
  "discipline_code": "VENT_UTF",
  "category_code": "hms",
  "chapter_number": "6.1",
  "content_type": "text",
  "content": "<h2>Formål</h2><p>...</p>"
}
\`\`\`

### Allowed \`document_type\`
\`policy\`, \`procedure\`, \`instruction\`, \`form\`, \`guidance\`, \`record\`, \`template\`

### Notes
- \`category_code\` must be lowercase \`[a-z0-9_]+\` (e.g. \`hms\`, \`ks\`, \`sys\`).
- \`content\` is HTML. Mark company-specific placeholders with \`<span style="color: red;">…</span>\`.
- Write clear Norwegian bokmål unless another language is requested.
- You may return only \`structure\` (no documents) to create taxonomy, or only \`documents\` that reference existing codes.
- If the user asks for a topic that already has a document, prefer suggesting an update (describe in a short note field is NOT supported — simply omit that document from \`documents\`) rather than creating a duplicate.

## Example

\`\`\`json
{
  "create_missing_structure": false,
  "structure": [],
  "documents": [
    {
      "title": "Bruk av åndedrettsvern",
      "description": "Instruks for valg og bruk av åndedrettsvern.",
      "document_type": "instruction",
      "discipline_code": "VENT_UTF",
      "category_code": "hms",
      "chapter_number": "6.1",
      "content_type": "text",
      "content": "<h2>Formål</h2><p>Sikre korrekt bruk av vern.</p>"
    }
  ]
}
\`\`\`
`;
}

/** Static fallback when data is not loaded yet. */
export const PROMANG_HSE_DOCUMENT_AI_GUIDE = buildHseDocumentAiGuide([], []);
